.bar {
  display: flex;
  flex-direction: column;
  gap: var(--nexus-space-md);
  padding-block: var(--nexus-space-md);
  border-block: 1px solid var(--nexus-border-subtle);
}

.sticky {
  position: sticky;
  z-index: 20;
  background: var(--nexus-bg);
}

/* Row one: primary axis at the start, actions at the end. Both keep their
   place no matter how many secondary filters row two ends up holding. */
.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--nexus-space-lg);
  min-height: var(--nexus-control-md);
}

.primary {
  display: flex;
  align-items: center;
  min-width: 0;
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  flex-shrink: 0;
}

/* Row two: the only part allowed to change height between facets. */
.secondary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--nexus-space-sm);
}

.group {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  flex-wrap: wrap;
}

.segment {
  gap: var(--nexus-space-lg);
}

.option {
  display: inline-flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  font-family: inherit;
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-medium);
  color: var(--nexus-text-muted);
  background: none;
  cursor: pointer;
  transition:
    color var(--nexus-transition-fast) ease,
    border-color var(--nexus-transition-fast) ease,
    background var(--nexus-transition-fast) ease;
}

.option:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.option:focus-visible {
  outline: 2px solid var(--nexus-border-focus);
  outline-offset: 2px;
}

/* Segment: an underlined text run. No box, so the primary axis of the page
   reads as navigation rather than as another chip among many. */
.segment .option {
  border: none;
  border-bottom: 1px solid transparent;
  border-radius: 0;
  padding: 0 0 3px;
}

.segment .option:hover:not(:disabled) {
  color: var(--nexus-text-secondary);
}

.segment .option.active {
  color: var(--nexus-text);
  font-weight: var(--nexus-weight-semibold);
  border-bottom-color: var(--nexus-text);
}

/* Pill: a filled chip for secondary axes.

   Filled rather than outlined because an outline alone did not read as a
   control: --nexus-border on --nexus-bg measures 1.34:1, far under the 3:1
   WCAG asks of a component boundary, so the pills looked like plain labels.
   A surface fill carries the affordance and the border just defines the edge. */
.pill .option,
.standalone {
  height: var(--nexus-control-md);
  padding-inline: var(--nexus-space-md);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-control);
  background: var(--nexus-surface);
  color: var(--nexus-text-secondary);
}

.pill .option:hover:not(:disabled),
.standalone:hover:not(:disabled) {
  color: var(--nexus-text);
  background: var(--nexus-surface-hover);
  border-color: var(--nexus-border-strong);
}

/* Selected inverts. Unmistakable, and still entirely greyscale — the discovery
   surfaces spend their one colour on the primary action, not on a filter. */
.pill .option.active,
.standalone.active {
  background: var(--nexus-text);
  border-color: var(--nexus-text);
  color: var(--nexus-bg);
  font-weight: var(--nexus-weight-semibold);
}

.pill .option.active:hover:not(:disabled),
.standalone.active:hover:not(:disabled) {
  background: var(--nexus-text-secondary);
  border-color: var(--nexus-text-secondary);
  color: var(--nexus-bg);
}

/* No `background` here: the shared `.pill .option, .standalone` rule above
   sets the surface fill, and re-declaring it after that block reset the
   toggle back to transparent while the game pills stayed filled. */
.standalone {
  display: inline-flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  font-family: inherit;
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-medium);
  cursor: pointer;
  flex-shrink: 0;
}

.optionIcon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.optionIcon > img {
  width: 20px;
  height: 20px;
  border-radius: var(--nexus-radius-sm);
  object-fit: cover;
}

.count {
  color: var(--nexus-text-muted);
  font-weight: var(--nexus-weight-normal);
  font-variant-numeric: tabular-nums;
}

.segment .option.active .count {
  color: var(--nexus-text-secondary);
}

/* A selected pill is inverted, so its count has to invert with it. */
.pill .option.active .count,
.standalone.active .count {
  color: var(--nexus-bg);
  opacity: 0.7;
}

@media (max-width: 768px) {
  /* The actions drop under the primary axis rather than squeezing beside it,
     but they still keep their own line — they never interleave with the pills. */
  .top {
    flex-direction: column;
    align-items: stretch;
    gap: var(--nexus-space-md);
  }

  .actions {
    justify-content: flex-start;
  }

  /* A segment is a bare text run, so its box is only as tall as a line — about
     21px, well under a thumb. Pad it out to a real target on touch widths
     without moving the underline off the text. */
  .segment .option {
    min-height: 44px;
    align-items: center;
    padding-block: 0 3px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .option {
    transition: none;
  }
}
